import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { CostCodeDto } from 'src/api/FiretrolApiClient';
import { useCostCodes } from 'src/api/queries/organization/useCostCodes';
import { useDeleteCostCode } from 'src/api/queries/organization/useCostCodeMutations';
import { OfficeList } from 'src/app/organization/OfficeList';
import { useDialogs } from 'src/hooks/useDialogs';
import { DeleteActionIcon } from 'src/design/molecules/DeleteActionIcon';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';
import { useTableExports } from 'src/design/organisms/useTableExports';

const getColumns = (): MRT_ColumnDef<CostCodeDto>[] => {
  return [
    {
      accessorKey: 'code',
      header: 'Code',
      size: 150,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      size: 300,
    },
    {
      id: 'offices',
      header: 'Offices',
      filterFn: 'contains',
      enableColumnFilterModes: false,
      accessorFn: (row) => {
        return row.offices?.map((o) => o.name).join(', ');
      },
      Cell: ({ cell }) => {
        const costCode = cell.row.original;
        return <OfficeList offices={costCode.offices ?? []} />;
      },
    },
    {
      id: 'usage',
      accessorFn: (row) => row.usage || 0,
      header: 'Usage',
      enableColumnFilter: false,
      Cell: ({ cell }) => {
        const costCode = cell.row.original;
        const usage = costCode.usage || 0;
        const color = usage > 0 ? 'blue' : 'gray';
        return <Badge color={color}>{`${usage} Accounts`}</Badge>;
      },
    },
  ];
};

export const CostCodes: React.FC = () => {
  const { data, isLoading, isFetching } = useCostCodes();
  const costCodes = data ?? [];
  const deleteCostCode = useDeleteCostCode();
  const { setCreateCostCodeDialogOpen } = useDialogs();

  const columns = useMemo(() => getColumns(), []);

  const tableState = useTableState({
    identifier: 'cost-codes',
    columns: columns,
    defaultSorting: [{ id: 'code', desc: false }],
  });

  const tableExports = useTableExports<CostCodeDto>('cost-codes', (data) => {
    return data.map((item) => ({
      ['Code']: item.code,
      ['Name']: item.name,
      ['Offices']: item.offices?.map((o) => o.name).join(' | ') || '',
      ['Usage']: `${item.usage || 0} Accounts`,
    }));
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 150 } },
      renderRowActions: ({ row }) => {
        const costCode = row.original;
        const usage = costCode.usage || 0;
        return (
          <Group>
            <EditActionIcon
              onClick={() => setCreateCostCodeDialogOpen(true, costCode)}
            />
            <DeleteActionIcon
              requiredConfirmation={true}
              onDelete={() => deleteCostCode.mutate(costCode)}
              disabled={usage > 0}
            />
          </Group>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: costCodes,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton onClick={() => setCreateCostCodeDialogOpen(true)}>
            Create Cost Code
          </CreateButton>
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: costCodes.length,
    isLoading,
    isFetching,
    isError: false,
    ...tableExports,
  });

  return (
    <>
      <Header
        breadcrumbProps={{
          items: [{ text: 'Settings' }, { text: 'Cost Codes' }],
        }}
        title="Cost Codes"
        helpText="Cost codes are used to categorize and track expenses in WBS accounts. They provide a standardized way to organize project costs across the organization."
      />

      {table}
    </>
  );
};
